/**
 * ServerManagementPage
 * 배포 서버 목록 및 상태 모니터링 페이지
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ServerFormModal } from './ServerFormModal';
import { ServerDetailModal } from './ServerDetailModal';

export interface DeploymentServer {
  id: string;
  server_code: string;
  customer_name: string;
  organization_id?: string;
  hostname?: string;
  local_ip?: string;
  tailscale_ip?: string;
  ssh_user?: string;
  ssh_port?: number;
  deploy_mode?: string;
  os_info?: string;
  hardware_info?: Record<string, any>;
  docker_services?: string[];
  status: string;
  last_seen_at?: string;
  last_health_check?: Record<string, any>;
  notes?: string;
  installed_at?: string;
  created_at: string;
  updated_at?: string;
}

interface ServerListResponse {
  success: boolean;
  servers: DeploymentServer[];
  total: number;
  stats: {
    total: number;
    online: number;
    offline: number;
    unknown: number;
    maintenance: number;
  };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  online: { label: '온라인', color: 'text-green-800', bg: 'bg-green-100' },
  offline: { label: '오프라인', color: 'text-red-800', bg: 'bg-red-100' },
  unknown: { label: '미확인', color: 'text-gray-800', bg: 'bg-gray-100' },
  maintenance: { label: '점검중', color: 'text-yellow-800', bg: 'bg-yellow-100' },
};

interface OrgInfo {
  id: string;
  name: string;
  service_type?: string;
}

export const ServerManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [servers, setServers] = useState<DeploymentServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, online: 0, offline: 0, unknown: 0, maintenance: 0 });
  const [orgMap, setOrgMap] = useState<Record<string, OrgInfo>>({});

  // 필터
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  // 모달
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingServer, setEditingServer] = useState<DeploymentServer | null>(null);

  // 상세보기 모달
  const [detailServer, setDetailServer] = useState<DeploymentServer | null>(null);

  // 헬스체크 진행 상태
  const [checking, setChecking] = useState(false);
  const [checkingServerId, setCheckingServerId] = useState<string | null>(null);

  const fetchServers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);

      const response = await fetch(`/api/admin/servers?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) throw new Error('서버 목록 조회 실패');

      const data: ServerListResponse = await response.json();
      setServers(data.servers || []);
      setStats(data.stats || { total: 0, online: 0, offline: 0, unknown: 0, maintenance: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await fetch('/api/v1/admin/owner-approvals/all', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const orgs = data.organizations || data || [];
        const map: Record<string, OrgInfo> = {};
        orgs.forEach((org: any) => {
          map[org.id] = { id: org.id, name: org.name, service_type: org.service_type };
        });
        setOrgMap(map);
      }
    } catch {
      // 조직 정보 로드 실패해도 서버 목록은 정상 표시
    }
  };

  useEffect(() => {
    fetchServers();
    fetchOrganizations();
  }, [statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchServers();
  };

  const handleHealthCheckAll = async () => {
    try {
      setChecking(true);
      const response = await fetch('/api/admin/servers/health-check-all', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) throw new Error('헬스체크 실패');

      await fetchServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : '헬스체크 오류');
    } finally {
      setChecking(false);
    }
  };

  const handleHealthCheckSingle = async (serverId: string) => {
    try {
      setCheckingServerId(serverId);
      const response = await fetch(`/api/admin/servers/${serverId}/health-check`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) throw new Error('헬스체크 실패');

      await fetchServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : '헬스체크 오류');
    } finally {
      setCheckingServerId(null);
    }
  };

  const handleDelete = async (server: DeploymentServer) => {
    if (!window.confirm(`'${server.server_code}' (${server.customer_name}) 서버를 삭제하시겠습니까?`)) return;

    try {
      const response = await fetch(`/api/admin/servers/${server.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!response.ok) throw new Error('삭제 실패');

      fetchServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : '삭제 오류');
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setEditingServer(null);
    fetchServers();
  };

  const handleEdit = (server: DeploymentServer) => {
    setEditingServer(server);
    setShowFormModal(true);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeSince = (dateStr?: string) => {
    if (!dateStr) return '확인 안됨';
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return '방금 전';
    if (minutes < 60) return `${minutes}분 전`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}시간 전`;
    const days = Math.floor(hours / 24);
    return `${days}일 전`;
  };

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">서버 관리</h1>
          <p className="text-sm text-gray-600 mt-1">고객 독서실 배포 서버 모니터링 및 관리</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleHealthCheckAll}
            disabled={checking}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {checking ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>확인 중...</span>
              </>
            ) : (
              <>
                <span>🔄</span>
                <span>전체 상태 확인</span>
              </>
            )}
          </button>
          <button
            onClick={() => {
              setEditingServer(null);
              setShowFormModal(true);
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <span>+</span>
            <span>서버 추가</span>
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div
          onClick={() => setStatusFilter(statusFilter ? '' : '')}
          className={`p-4 rounded-lg border cursor-pointer transition-all bg-white ${!statusFilter ? 'ring-2 ring-indigo-500' : ''}`}
        >
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">전체</div>
        </div>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <div
            key={key}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${config.bg} ${
              statusFilter === key ? 'ring-2 ring-indigo-500' : ''
            }`}
          >
            <div className={`text-2xl font-bold ${config.color}`}>
              {stats[key as keyof typeof stats] || 0}
            </div>
            <div className={`text-sm ${config.color}`}>{config.label}</div>
          </div>
        ))}
      </div>

      {/* 검색 */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">전체 상태</option>
            {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <div className="flex-1 flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="서버코드, 고객사, IP 검색"
              className="flex-1 px-3 py-2 border rounded-lg"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              검색
            </button>
          </div>
        </form>
      </div>

      {/* 에러 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* 서버 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">로딩 중...</div>
        ) : servers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            등록된 서버가 없습니다.
            <button
              onClick={() => setShowFormModal(true)}
              className="block mx-auto mt-4 text-indigo-600 hover:underline"
            >
              서버 추가하기
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">서버 코드</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">고객사</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">상태</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Tailscale IP</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">마지막 확인</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">배포 모드</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {servers.map((server) => {
                  const statusInfo = STATUS_CONFIG[server.status] || STATUS_CONFIG.unknown;
                  const isChecking = checkingServerId === server.id;

                  return (
                    <tr key={server.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setDetailServer(server)}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{server.server_code}</div>
                        {server.hostname && server.hostname !== server.server_code && (
                          <div className="text-xs text-gray-500">{server.hostname}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{server.customer_name}</div>
                        {server.organization_id && orgMap[server.organization_id] ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate('/admin/businesses/pending'); }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                            title="사업자 관리로 이동"
                          >
                            {orgMap[server.organization_id].name}
                          </button>
                        ) : server.os_info ? (
                          <div className="text-xs text-gray-500">{server.os_info}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        {server.last_health_check?.response_time_ms && (
                          <div className="text-xs text-gray-500 mt-1">
                            {Math.round(server.last_health_check.response_time_ms)}ms
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 font-mono">{server.tailscale_ip || '-'}</div>
                        {server.local_ip && (
                          <div className="text-xs text-gray-500 font-mono">{server.local_ip}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{getTimeSince(server.last_seen_at)}</div>
                        {server.last_seen_at && (
                          <div className="text-xs text-gray-500">{formatDate(server.last_seen_at)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{server.deploy_mode || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleHealthCheckSingle(server.id); }}
                            disabled={isChecking}
                            className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"
                            title="상태 확인"
                          >
                            {isChecking ? '...' : '🔍'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(server); }}
                            className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                            title="수정"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(server); }}
                            className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                            title="삭제"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 상세보기 모달 */}
      {detailServer && (
        <ServerDetailModal
          server={detailServer}
          orgMap={orgMap}
          onClose={() => setDetailServer(null)}
          onEdit={(s) => {
            setDetailServer(null);
            handleEdit(s);
          }}
          onDelete={(s) => {
            setDetailServer(null);
            handleDelete(s);
          }}
          onHealthCheck={(id) => {
            handleHealthCheckSingle(id);
          }}
        />
      )}

      {/* 폼 모달 */}
      {showFormModal && (
        <ServerFormModal
          server={editingServer}
          onClose={() => {
            setShowFormModal(false);
            setEditingServer(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};
